import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { sortPeriods } from "@/features/S2Dashboard/model/utils";
import {
  fundSelectionKey,
  r1Api,
  type R1Fund,
  type R1FundSelection,
} from "../services/api";

// ─── Session storage helpers ──────────────────────────────────────────────────

const STORAGE_FUND_IDS = "r1_selectedFundIds";
const STORAGE_FUND_SCOPE = "r1_fundScope";
const STORAGE_PERIOD = "r1_selectedPeriod";

function readStoredFundIds(): number[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_FUND_IDS);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function readStoredPeriod(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_PERIOD) ?? null;
  } catch {
    return null;
  }
}

function readStoredFundScope(): "all" | "ids" {
  try {
    const raw = sessionStorage.getItem(STORAGE_FUND_SCOPE);
    return raw === "all" ? "all" : "ids";
  } catch {
    return "ids";
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface R1DashboardContextValue {
  funds: R1Fund[];
  fundsLoading: boolean;
  setFundsModalOpen: (open: boolean) => void;
  selectedFundIds: number[];
  fundSelection: R1FundSelection;
  setSelectedFundIds: (ids: number[]) => void;
  setFundSelection: (selection: R1FundSelection) => void;
  selectedPeriod: string | null;
  setSelectedPeriod: (period: string | null) => void;
  /** Union of all available periods across selected funds, sorted descending */
  availablePeriods: string[];
  periodsLoading: boolean;
  /** Per-fund available periods, sorted descending. Used for "most recent per fund" filtering. */
  periodsPerFund: Map<number, string[]>;
}

const R1DashboardContext = createContext<R1DashboardContextValue | null>(null);

export function R1DashboardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const isMastersheetRoute = location.pathname.includes("/r1_mastersheet");
  const [selectedFundIds, setSelectedFundIdsState] =
    useState<number[]>(readStoredFundIds);
  const [fundSelection, setFundSelectionState] = useState<R1FundSelection>(
    () => {
      const storedIds = readStoredFundIds();
      const storedScope = readStoredFundScope();
      if (storedScope === "all") return { mode: "all" };
      return { mode: "ids", ids: storedIds };
    },
  );
  const [selectedPeriod, setSelectedPeriodState] = useState<string | null>(
    readStoredPeriod,
  );

  const setSelectedFundIds = (ids: number[]) => {
    setSelectedFundIdsState(ids);
    setFundSelectionState({ mode: "ids", ids });
    sessionStorage.setItem(STORAGE_FUND_IDS, JSON.stringify(ids));
    sessionStorage.setItem(STORAGE_FUND_SCOPE, "ids");
  };

  const setFundSelection = (selection: R1FundSelection) => {
    setFundSelectionState(selection);
    if (selection.mode === "all") {
      const allIds = funds.map((f) => Number(f.id ?? f.fund_id));
      setSelectedFundIdsState(allIds);
      sessionStorage.setItem(STORAGE_FUND_IDS, JSON.stringify(allIds));
      sessionStorage.setItem(STORAGE_FUND_SCOPE, "all");
      return;
    }
    setSelectedFundIdsState(selection.ids);
    sessionStorage.setItem(STORAGE_FUND_IDS, JSON.stringify(selection.ids));
    sessionStorage.setItem(STORAGE_FUND_SCOPE, "ids");
  };

  const setSelectedPeriod = (period: string | null) => {
    // Invalidate only queries keyed to the outgoing period before switching
    if (selectedPeriod && selectedPeriod !== period) {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key[0] === "r1" &&
            key.includes(selectedPeriod)
          );
        },
      });
    }
    setSelectedPeriodState(period);
    if (period) sessionStorage.setItem(STORAGE_PERIOD, period);
    else sessionStorage.removeItem(STORAGE_PERIOD);
  };

  const [fundsModalOpen, setFundsModalOpen] = useState(false);

  // Selected funds only — always fetched for chip display in the filter bar
  const sortedSelectedIds = useMemo(
    () => [...selectedFundIds].sort((a, b) => a - b),
    [selectedFundIds],
  );
  const { data: selectedFundsData } = useQuery({
    queryKey: ["r1", "funds-selected", sortedSelectedIds],
    queryFn: () => r1Api.getFunds({ slim: true, ids: sortedSelectedIds }),
    enabled: sortedSelectedIds.length > 0,
    staleTime: 300_000,
  });

  // All funds — fetched only when the picker modal is opened for the first time
  const { data: allFundsData, isLoading: fundsLoading } = useQuery({
    queryKey: ["r1", "funds-slim"],
    queryFn: () => r1Api.getFunds({ slim: true }),
    enabled: fundsModalOpen,
    staleTime: 300_000,
  });

  const funds = useMemo<R1Fund[]>(() => {
    if (allFundsData?.data?.length) return allFundsData.data as R1Fund[];
    return (selectedFundsData?.data ?? []) as R1Fund[];
  }, [allFundsData, selectedFundsData]);

  useEffect(() => {
    // Only run when we have the full fund list — not just the selected-funds slim set.
    if (!allFundsData?.data?.length) return;
    if (fundSelection.mode !== "ids") return;
    const uniqueIds = new Set(selectedFundIds);
    if (uniqueIds.size !== funds.length) return;
    const allFundIds = new Set(funds.map((f) => Number(f.id ?? f.fund_id)));
    const isExactAll = Array.from(uniqueIds).every((id) => allFundIds.has(id));
    if (!isExactAll) return;
    setFundSelectionState({ mode: "all" });
    sessionStorage.setItem(STORAGE_FUND_SCOPE, "all");
  }, [funds, fundSelection.mode, selectedFundIds, allFundsData]);

  // Single batch request for all selected funds — replaces N parallel requests
  const fundScopeKey = fundSelectionKey(fundSelection);
  const { data: periodsData, isLoading: periodsLoading } = useQuery({
    queryKey: ["r1", "reporting-periods-per-fund", fundScopeKey],
    queryFn: () => r1Api.getReportingPeriodsPerFund(fundSelection),
    enabled: selectedFundIds.length > 0 && !isMastersheetRoute,
    staleTime: 60_000,
  });

  const periodsPerFund = useMemo(() => {
    const map = new Map<number, string[]>();
    if (!periodsData) return map;
    for (const id of selectedFundIds) {
      const raw = periodsData[String(id)] ?? [];
      map.set(id, sortPeriods(raw).reverse());
    }
    return map;
  }, [periodsData, fundScopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const allPeriods = useMemo(() => {
    const set = new Set<string>();
    for (const periods of periodsPerFund.values()) {
      for (const p of periods) set.add(p);
    }
    return sortPeriods(Array.from(set)).reverse();
  }, [periodsPerFund]);

  // Auto-select latest available period when available periods change.
  useEffect(() => {
    if (allPeriods.length === 0) return;
    if (!selectedPeriod || !allPeriods.includes(selectedPeriod)) {
      setSelectedPeriod(allPeriods[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPeriods.join(",")]);

  return (
    <R1DashboardContext.Provider
      value={{
        funds,
        fundsLoading,
        setFundsModalOpen,
        selectedFundIds,
        fundSelection,
        setSelectedFundIds,
        setFundSelection,
        selectedPeriod,
        setSelectedPeriod,
        availablePeriods: allPeriods,
        periodsLoading,
        periodsPerFund,
      }}
    >
      {children}
    </R1DashboardContext.Provider>
  );
}

export function useR1DashboardContext() {
  const ctx = useContext(R1DashboardContext);
  if (!ctx)
    throw new Error(
      "useR1DashboardContext must be used within R1DashboardProvider",
    );
  return ctx;
}
